import Alpine from 'alpinejs';
import type { CellComponent } from 'tabulator-tables';
import { TabulatorFull as Tabulator } from 'tabulator-tables';

import {
    datesSorter,
    icon,
    linkFormatter,
    populateSelect,
    readData,
    toOptions,
    uniqueValues,
    wireFilters,
} from '../tabulator';

const hash = window.location.hash;
if (hash) {
    const tabId = hash.slice(1);
    const tabRoot = document.getElementById('dashboard-tabs');
    if (tabRoot && document.getElementById(tabId)) {
        const data = Alpine.$data(tabRoot) as { active: string };
        data.active = tabId;
    }
}

function getCsrfToken(): string {
    for (const cookie of document.cookie.split(';')) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'csrftoken') return decodeURIComponent(value);
    }
    return '';
}

function dateFormatter(cell: CellComponent): string {
    const date = new Date(cell.getValue() as string);
    const shortDate = date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `<span x-tooltip.placement.right="'${time}'">${shortDate}</span>`;
}

function authorFormatter(cell: CellComponent): string {
    const row = cell.getRow().getData() as { portfolio: string };
    return `<a href="${row.portfolio}">${cell.getValue()}</a>`;
}

function reviewRequestAuthorFormatter(cell: CellComponent): string {
    const row = cell.getRow().getData() as { submission: { portfolio: string } };
    return `<a href="${row.submission.portfolio}">${cell.getValue()}</a>`;
}

function reviewLinkFormatter(cell: CellComponent): string {
    const row = cell.getRow().getData() as { dismiss: string };
    return `
        <form action="${row.dismiss}" class="flex items-center" method="post">
            <input type="hidden" name="csrfmiddlewaretoken" value="${getCsrfToken()}">
            <a href="${cell.getValue()}" class="inline-flex items-center justify-center border border-primary text-primary hover:bg-primary hover:text-white h-7 w-7 rounded-md transition-colors" x-tooltip.placement.top="'View'">
                ${icon('eye')}
            </a>
            <button class="inline-flex items-center justify-center border border-red-500 text-red-600 hover:bg-red-500 hover:text-white h-7 w-7 rounded-md xl:ml-1 transition-colors" x-tooltip.placement.top="'Dismiss'">
                ${icon('circle-x')}
            </button>
        </form>
    `;
}

function reviewedFormatter(cell: CellComponent): string {
    const value = cell.getValue() as Array<{ author: string; date: string }>;
    if (value.length > 0) {
        const reviews = value.map((r) => `<p class="mb-0">${r.author} (${r.date})</p>`).join('');
        return icon('circle-check').replace(
            '<svg',
            `<svg x-tooltip.html.placement.right='\`${reviews}\`'`,
        );
    }
    return '';
}

function initSubmissionTable(el: HTMLElement): void {
    const src = el.dataset.src!;
    const data = readData(src);
    const table = new Tabulator(el, {
        data,
        pagination: true,
        paginationSize: 10,
        layout: 'fitColumns',
        initialSort: [{ column: 'submitted', dir: 'desc' }],
        columns: [
            { title: 'Title', field: 'title', widthGrow: 35 },
            { title: 'Site', field: 'site', widthGrow: 35 },
            {
                title: 'Submitted',
                field: 'submitted',
                sorter: datesSorter,
                formatter: dateFormatter,
                widthGrow: 20,
            },
            {
                title: 'Reviewed',
                field: 'reviewed',
                formatter: reviewedFormatter,
                headerSort: false,
                hozAlign: 'center',
                width: 100,
                minWidth: 100,
            },
            {
                title: '',
                field: 'url',
                formatter: linkFormatter,
                headerSort: false,
                width: 90,
                minWidth: 90,
            },
        ],
    });
    const siteSelect = document.getElementById(`filter-site-${src}`) as HTMLSelectElement;
    populateSelect(siteSelect, toOptions(uniqueValues(data, 'site')));
    wireFilters(table, [{ select: siteSelect, field: 'site' }]);
}

function initSubmissionWithAuthorTable(el: HTMLElement): void {
    const src = el.dataset.src!;
    const data = readData(src);
    const table = new Tabulator(el, {
        data,
        pagination: true,
        paginationSize: 10,
        layout: 'fitColumns',
        initialSort: [{ column: 'submitted', dir: 'desc' }],
        columns: [
            { title: 'Title', field: 'title', widthGrow: 35 },
            { title: 'Site', field: 'site', widthGrow: 20 },
            { title: 'Author', field: 'author', formatter: authorFormatter, widthGrow: 20 },
            {
                title: 'Submitted',
                field: 'submitted',
                sorter: datesSorter,
                formatter: dateFormatter,
                widthGrow: 20,
            },
            {
                title: '',
                field: 'url',
                formatter: linkFormatter,
                headerSort: false,
                width: 80,
                minWidth: 80,
            },
        ],
    });
    const siteSelect = document.getElementById(`filter-site-${src}`) as HTMLSelectElement;
    const authorSelect = document.getElementById(`filter-author-${src}`) as HTMLSelectElement;
    populateSelect(siteSelect, toOptions(uniqueValues(data, 'site')));
    populateSelect(authorSelect, toOptions(uniqueValues(data, 'author')));
    wireFilters(table, [
        { select: siteSelect, field: 'site' },
        { select: authorSelect, field: 'author' },
    ]);
}

function initReviewRequestTable(el: HTMLElement): void {
    const src = el.dataset.src!;
    const data = readData(src);
    const table = new Tabulator(el, {
        data,
        pagination: true,
        paginationSize: 10,
        layout: 'fitColumns',
        initialSort: [{ column: 'submission.submitted', dir: 'desc' }],
        columns: [
            { title: 'Title', field: 'submission.title', widthGrow: 30 },
            { title: 'Site', field: 'submission.site', widthGrow: 20 },
            {
                title: 'Author',
                field: 'submission.author',
                formatter: reviewRequestAuthorFormatter,
                widthGrow: 20,
            },
            {
                title: 'Submitted',
                field: 'submission.submitted',
                sorter: datesSorter,
                formatter: dateFormatter,
                widthGrow: 20,
            },
            {
                title: '',
                field: 'submission.url',
                formatter: reviewLinkFormatter,
                headerSort: false,
                width: 110,
                minWidth: 110,
            },
        ],
    });
    const siteSelect = document.getElementById(`filter-site-${src}`) as HTMLSelectElement;
    const authorSelect = document.getElementById(`filter-author-${src}`) as HTMLSelectElement;
    populateSelect(siteSelect, toOptions(uniqueValues(data, 'submission.site')));
    populateSelect(authorSelect, toOptions(uniqueValues(data, 'submission.author')));
    wireFilters(table, [
        { select: siteSelect, field: 'submission.site' },
        { select: authorSelect, field: 'submission.author' },
    ]);
}

window.addEventListener('load', () => {
    document
        .querySelectorAll<HTMLElement>('[data-table-type="submission"]')
        .forEach(initSubmissionTable);
    document
        .querySelectorAll<HTMLElement>('[data-table-type="submission-with-author"]')
        .forEach(initSubmissionWithAuthorTable);
    document
        .querySelectorAll<HTMLElement>('[data-table-type="review-request"]')
        .forEach(initReviewRequestTable);
});
